import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Accordion as MuiAccordion, AccordionDetails, AccordionSummary, Stack, styled, Typography } from '@mui/material'
import DOMPurify from 'dompurify'
import type { FC } from 'react'
import { useState } from 'react'

import { COLORS, ICONS, optimizelyCss, unit } from '@hcr/ui'

import { usePointsExchangeInfoQuery } from '../../../hooks'
import { Path } from '../../../models/navigation'
import { to } from '../../../utils/navigation'
import { LayoutNavigationBackError500 } from '../../common/layout-navigation-back'

import { InternalPointsExchangeNutshellLoading } from './InternalPointsExchangeNutshellLoading'

const BACK_NAVIGATION_PATH = to(Path.Home)

export const InternalPointsExchangeNutshell: FC = () => {
  const [expanded, setExpanded] = useState(true)
  const ipeNutshellInfo = usePointsExchangeInfoQuery()

  if (ipeNutshellInfo.isError) {
    return <LayoutNavigationBackError500 to={BACK_NAVIGATION_PATH} />
  }

  const renderAccordionSummary = (isExpanded: boolean) => (
    <StyledAccordionSummary
      expandIcon={<FontAwesomeIcon color={COLORS.black} icon={isExpanded ? ICONS.fassMinus : ICONS.fassPlus} />}
      aria-controls='points-exchange-accordion-summary'
      id='points-exchange-accordion-summary'
      aria-expanded={expanded}
    >
      <Typography variant='headlineExtraExtraSmall'>{ipeNutshellInfo.data?.title}</Typography>
    </StyledAccordionSummary>
  )

  if (ipeNutshellInfo.isSuccess && ipeNutshellInfo.data !== undefined) {
    return (
      <Stack>
        <StyledAccordion
          expanded={expanded}
          onChange={() => setExpanded(!expanded)}
          disableGutters
          elevation={0}
          square
        >
          {renderAccordionSummary(expanded)}
          <StyledAccordionDetails id={'points-exchange-accordion-summary'}>
            <StyledDescription
              variant='bodyM'
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(ipeNutshellInfo.data?.description) }}
            />
          </StyledAccordionDetails>
        </StyledAccordion>
      </Stack>
    )
  }

  return <InternalPointsExchangeNutshellLoading />
}

const StyledAccordionDetails = styled(AccordionDetails)`
  padding: 0 ${unit(4.25)} ${unit(4.25)} ${unit(4.25)};
`

const StyledDescription = styled(Typography)`
  ${optimizelyCss}

  strong {
    display: block;
    margin-top: ${unit(6)};
    font-family: ${({ theme }) => theme.typography.titleM.fontFamily};
    font-size: ${({ theme }) => theme.typography.titleM.fontSize};
    font-weight: ${({ theme }) => theme.typography.titleM.fontWeight};
    line-height: ${({ theme }) => theme.typography.titleM.lineHeight};
    letter-spacing: ${({ theme }) => theme.typography.titleM.letterSpacing};
  }

  em {
    display: block;
    font-style: normal;
    margin-top: ${unit(3.5)};
    font-family: ${({ theme }) => theme.typography.labelM.fontFamily};
    font-size: ${({ theme }) => theme.typography.labelM.fontSize};
    font-weight: ${({ theme }) => theme.typography.labelM.fontWeight};
    line-height: ${({ theme }) => theme.typography.labelM.lineHeight};
    letter-spacing: ${({ theme }) => theme.typography.labelM.letterSpacing};
  }
`

const StyledAccordion = styled(MuiAccordion)`
  background-color: ${COLORS.vaalea['60%']};
`

const StyledAccordionSummary = styled(AccordionSummary)`
  svg {
    width: ${unit(5)};
    height: ${unit(5)};
    color: ${COLORS.black};
  }

  margin: 0;
  padding: ${unit(5)} ${unit(4)} ${unit(5.5)} ${unit(4)};

  .MuiAccordionSummary-content {
    margin: 0;
  }
`
